<template>
  <modal name="agreement-alarm" class="modal-inner" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="400" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true">
    <span class="close-button" @click="hide('agreement-alarm')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("my_agreement.agreement_alarms") }}<span v-if="agreementNumber"> - {{ agreementNumber }}</span>
    </div>
    <div class="modal-body less-bottom-padding">
      <div v-if="isLoading" class="border border-gray-200 rounded-lg mt-3 py-3 px-3 font-sm text-gray-500 text-center">
        <BaseIcon icon="spinner" spin />
      </div>
      <div v-if="customers && customers.length > 0">
        <div class="flex">
          <div class="w-full clearfix">
            <div class="float-right flex flex-wrap">
              <!-- Excel export of every device on the agreement; only shown when there is something to export -->
              <button v-if="alarmCount > 0" class="btn-blue-outline mt-3 sm:mt-0 mr-2" @click.prevent="exportAlarms()">
                {{ $t("my_agreement.export_alarms") }}
                <BaseIcon icon="file-excel" class="ml-1" />
              </button>
              <button class="btn-secondary-outline mt-3 sm:mt-0" @click.prevent="expandUnits()">
                <span v-if="!all_expanded">
                  {{ $t("expand") }}
                  <BaseIcon icon="plus-circle" class="ml-1" />
                </span>
                <span v-else>
                  {{ $t("minimize") }}
                  <BaseIcon icon="minus-circle" class="ml-1" />
                </span>
              </button>
            </div>
          </div>
        </div>
        <div class="grid border border-gray-200 rounded-lg mt-3 py-3 px-3" v-for="customer in customers" :key="customer._id">
          <!-- customer name opens the customer's unit list -->
          <div class="text-md font-serif font-semibold text-primary-500 mb-2">
            <span v-if="customer.customer_id" class="cursor-pointer hover:underline" :title="customerRoute(customer)" @click.prevent="goTo(customerRoute(customer))">{{ customer.name }}</span>
            <span v-else>{{ customer.name }}</span>
          </div>
          <div class="grid mt-1" v-for="(unit, index) in customer.units" :key="index">
            <div
              class="clearfix cursor-pointer"
              @click.prevent="
                unit.isAlarmVisible = !unit.isAlarmVisible;
                checkExpandUnit();
              "
            >
              <div class="float-left text-sm font-semibold">
                {{ unit.name }}
                <span class="text-xs text-gray-400"> ( {{ unit.alarms.length }} {{ $t("my_agreement.alarm") }} )</span>
              </div>
              <div class="float-right" v-if="unit.alarms.length > 0">
                <BaseIcon v-if="!unit.isAlarmVisible" class="text-sm text-primary-500" icon="plus-circle" />
                <BaseIcon v-if="unit.isAlarmVisible" class="text-sm text-primary-500" icon="minus-circle" />
              </div>
            </div>
            <div class="mb-1" v-show="unit.isAlarmVisible">
              <div class="grid" v-for="(alarm, c) in unit.alarms" :key="c">
                <div class="pb-1 clearfix">
                  <div class="float-left">
                    <span class="text-xs font-medium"> {{ alarm.unit_name }} - {{ alarm.modelnumber }} </span>
                  </div>
                  <div class="float-right flex items-center">
                    <span class="text-xs font-medium">
                      {{ formatDate(alarm.contract_start) }}
                    </span>
                    <!-- same view as "Visa" in the unit's alarm list: the alarms page
                         opens the alarm info modal when the alarm id is in the route -->
                    <button v-if="customer.customer_id && unit.unit_id && alarm._id" class="btn-blue-outline text-xs py-0 px-2 ml-3" :title="alarmRoute(customer, unit, alarm)" @click.prevent.stop="goTo(alarmRoute(customer, unit, alarm))">{{ $t("view") }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="customers && customers.length == 0" class="border border-gray-200 rounded-lg mt-3 py-3 px-3 font-sm text-gray-500 text-center">
        {{ $t("my_agreement.no_alarm_found") }}
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 800;

export default {
  props: {
    agreementNumber: {
      required: true,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      all_expanded: false,
      customers: null,
    };
  },

  watch: {
    agreementNumber(newVal) {
      if (newVal != null) {
        this.customers = null;
        this.all_expanded = false;
        this.getAgreementAlarms();
      }
    },
  },

  computed: {
    user() {
      return this.$store.state.user;
    },

    // The customer/unit/alarm routes take the viewer's role as :type and the
    // role's own id as :id, same as sidebar_menu.getId(). Admins browse as
    // "customer" with "-".
    routeTypeAndId() {
      const u = this.user || {};
      if (u.role === "seller") return { type: "seller", id: u.seller_id || "-" };
      if (u.role === "reseller") return { type: "reseller", id: u.reseller_id || "-" };
      return { type: "customer", id: "-" };
    },

    alarmCount() {
      if (!this.customers) return 0;
      return this.customers.reduce((sum, customer) => sum + customer.units.reduce((s, unit) => s + (unit.alarms ? unit.alarms.length : 0), 0), 0);
    },
  },

  methods: {
    getAgreementAlarms() {
      this.isLoading = true;

      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/agreement/alarms/${this.agreementNumber}`)
        .then((response) => {
          this.customers = response.data.customers;
          this.isLoading = false;
        })
        .catch((error) => {
          this.handleError(error);
          this.isLoading = false;
        });
    },

    expandUnits() {
      this.all_expanded = !this.all_expanded;
      this.customers.forEach((customer) => {
        customer.units.forEach((unit) => {
          if (this.all_expanded) unit.isAlarmVisible = true;
          else unit.isAlarmVisible = false;
        });
      });
    },

    checkExpandUnit() {
      let all_expanded = false;
      this.customers.forEach((customer) => {
        customer.units.forEach((unit) => {
          if (unit.isAlarmVisible) all_expanded = true;
        });
      });

      if (all_expanded) this.all_expanded = true;
      else this.all_expanded = false;
    },

    formatDate(date) {
      return this.moment(date).format("YYYY-MM-DD");
    },

    customerRoute(customer) {
      const { type, id } = this.routeTypeAndId;
      return `/verksamheter/${type}/${customer.customer_id}/${id}`;
    },

    alarmRoute(customer, unit, alarm) {
      const { type, id } = this.routeTypeAndId;
      return `/larm/${type}/${customer.customer_id}/${unit.unit_id}/${id}/${alarm._id}`;
    },

    // New tab, so the agreement list and this modal stay where the user left them.
    goTo(path) {
      window.open(this.$router.resolve(path).href, "_blank", "noopener");
    },

    // One row per device. Key order = column order in the sheet:
    // enhetstyp, IMEI, telematiknummer, serienummer, enhetsnamn, verksamhet, kund, avtalsnummer
    exportAlarms() {
      const labels = {
        type: this.$t("my_agreement.export_device_type"),
        imei: this.$t("my_agreement.export_imei"),
        phone: this.$t("my_agreement.export_telematics_number"),
        serial: this.$t("my_agreement.export_serial_number"),
        name: this.$t("my_agreement.export_unit_name"),
        business: this.$t("my_agreement.export_business"),
        customer: this.$t("my_agreement.export_customer"),
        agreement: this.$t("my_agreement.export_agreement_number"),
      };

      const rows = [];
      (this.customers || []).forEach((customer) => {
        (customer.units || []).forEach((unit) => {
          (unit.alarms || []).forEach((alarm) => {
            rows.push({
              [labels.type]: alarm.type || "",
              [labels.imei]: alarm.imei_number || "",
              [labels.phone]: alarm.phone || "",
              [labels.serial]: alarm.modelnumber || "",
              [labels.name]: alarm.unit_name || "",
              [labels.business]: unit.name || "",
              [labels.customer]: customer.name || "",
              [labels.agreement]: this.agreementNumber || "",
            });
          });
        });
      });

      // excelExport (global helpers mixin) names the file <date>_<fileName>.xlsx and triggers the download
      const safeNumber = String(this.agreementNumber || "agreement").replace(/[^A-Za-z0-9_-]/g, "_");
      this.excelExport(rows, this.$t("my_agreement.agreement_alarms"), `agreement_${safeNumber}_devices`, "xlsx");
    },

    hide(id) {
      this.$modal.hide(id);
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },
};
</script>
